'use client';
import type { Assign } from '@ark-ui/react';
import { Tabs } from '@ark-ui/react';
import { type TabsVariantProps, tabs } from '@terra/ui-styled-system';
import type { ComponentProps, HTMLStyledProps } from '@terra/ui-styled-system';
import {
  BIEvents,
  type BIEventProps,
  serializeBIEventData,
} from '@terra/ui-telemetry/bi-analytics';
import { forwardRef } from 'react';
import { createStyleContext } from './utils/create-style-context';

const { withProvider, withContext } = createStyleContext(tabs);

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, Tabs.RootProviderProps>,
    TabsVariantProps
  >
>(Tabs.RootProvider, 'root');

export type RootProps = ComponentProps<typeof Root>;
export const Root = withProvider<
  HTMLDivElement,
  Assign<Assign<HTMLStyledProps<'div'>, Tabs.RootProps>, TabsVariantProps>
>(Tabs.Root, 'root');

export const Content = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Tabs.ContentProps>
>(Tabs.Content, 'content');

export const Indicator = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Tabs.IndicatorProps>
>(Tabs.Indicator, 'indicator');

export const List = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Tabs.ListProps>
>(Tabs.List, 'list');

type TriggerAnalyticsProps = {
  bi?: BIEventProps;
};

const BaseTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Tabs.TriggerProps>
>(Tabs.Trigger, 'trigger');

export type TriggerProps = ComponentProps<typeof BaseTrigger> &
  TriggerAnalyticsProps;

export const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  (props, ref) => {
    const { bi, ...rest } = props;
    const { eventType = BIEvents.TAB, eventData } = bi || {};

    return (
      <BaseTrigger
        ref={ref}
        data-bi-unmask="true"
        data-analytics-type={eventType}
        data-analytics-props={serializeBIEventData(eventData)}
        {...rest}
      />
    );
  },
);

Trigger.displayName = 'Tabs.Trigger';

export { TabsContext as Context } from '@ark-ui/react';
